import { Injectable } from "@angular/core";
import { TopUpIntentType } from "../../../interfaces/common/payment-enums";

declare const Stripe: any;

type StripeFieldKey = "number" | "expiry" | "cvc";

interface StripeFieldState {
  touched: boolean;
  empty: boolean;
  complete: boolean;
  error: string;
}

@Injectable()
export class StripeContextService {

  private stripe: any;

  private elements: any;

  private cardNumberElement: any;

  private cardExpiryElement: any;

  private cardCvcElement: any;

  private currentKey = "";

  stripeState: Record<StripeFieldKey, StripeFieldState> = {
    number: { touched: false, empty: true, complete: false, error: "" },
    expiry: { touched: false, empty: true, complete: false, error: "" },
    cvc: { touched: false, empty: true, complete: false, error: "" },
  };

  mount(stripeKey: string): string {
    if (typeof Stripe !== "function")
      return "Stripe.js failed to load.";

    if (!stripeKey)
      return "Stripe key not configured.";

    if (!this.stripe || this.currentKey !== stripeKey)
      this.initialiseStripe(stripeKey);

    this.initialiseElements();

    return "";
  }

  private initialiseStripe(stripeKey: string) {
    this.stripe = Stripe(stripeKey);
    this.currentKey = stripeKey;
  }

  private initialiseElements() {
    this.destroyElements();
    this.elements = this.stripe.elements();

    const style = { base: { fontSize: "14px" } };

    this.cardNumberElement = this.elements.create("cardNumber", { style, disableLink: true });
    this.cardExpiryElement = this.elements.create("cardExpiry", { style });
    this.cardCvcElement = this.elements.create("cardCvc", { style });
    
    this.stripeState.number = { touched: false, empty: true, complete: false, error: "" };
    this.stripeState.expiry = { touched: false, empty: true, complete: false, error: "" };
    this.stripeState.cvc = { touched: false, empty: true, complete: false, error: "" };

    this.cardNumberElement.mount("#stripe-card-number");
    this.cardExpiryElement.mount("#stripe-card-expiry");
    this.cardCvcElement.mount("#stripe-card-cvc");

    this.bindField(this.cardNumberElement, "number");
    this.bindField(this.cardExpiryElement, "expiry");
    this.bindField(this.cardCvcElement, "cvc");
  }

  private destroyElements() {
    try { this.cardNumberElement?.unmount(); } catch {}
    try { this.cardExpiryElement?.unmount(); } catch {}
    try { this.cardCvcElement?.unmount(); } catch {}
    try { this.cardNumberElement?.destroy(); } catch {}
    try { this.cardExpiryElement?.destroy(); } catch {}
    try { this.cardCvcElement?.destroy(); } catch {}
    this.cardNumberElement = null;
    this.cardExpiryElement = null;
    this.cardCvcElement = null;
  }

  private bindField(element: any, key: StripeFieldKey) {
    element.on("change", (event: any) => {
      this.stripeState[key].empty = !!event.empty;
      this.stripeState[key].complete = !!event.complete;
      this.stripeState[key].error = event.error?.message ?? "";
    });

    element.on("blur", () => {
      this.stripeState[key].touched = true;
    });
  }

  markAllAsTouched() {
    this.stripeState.number.touched = true;
    this.stripeState.expiry.touched = true;
    this.stripeState.cvc.touched = true;
  }

  isCardValid(): boolean {
    return this.stripeState.number.complete && !this.stripeState.number.error && this.stripeState.expiry.complete && !this.stripeState.expiry.error && this.stripeState.cvc.complete && !this.stripeState.cvc.error;
  }

  public async confirmCard(
    request: any,
    billingDetails: any,
    returnUrl: string
  ): Promise<any> {
    if (!this.stripe)
      return { ok: false, message: "Stripe is not initialised." };

    try {
      let result: any;

      if (request.intentType === TopUpIntentType.PaymentIntent) {
        result = await this.stripe.confirmCardPayment(request.clientSecret, {
          payment_method: {
            card: this.cardNumberElement,
            billing_details: billingDetails,
          },
          return_url: returnUrl,
        });
      } else if (request.intentType === TopUpIntentType.SetupIntent) {
        result = await this.stripe.confirmCardSetup(request.clientSecret, {
          payment_method: {
            card: this.cardNumberElement,
            billing_details: billingDetails,
          },
          return_url: returnUrl,
        });
      } else
        return { ok: false, message: "Unsupported intent type" };

      if (result?.error)
        return {
          ok: false,
          message: result.error.message || "Card confirmation failed",
        };

      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.message || "Card confirmation failed" };
    }
  }
}
